import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowLeft, Send } from "lucide-react";
import { Button } from "../ui/button";
import { API_URL } from "../../lib/config";
import { ImageCarousel } from "../ImageCarousel";
import { MarkdownText } from "../MarkdownText";

export function ConfirmStep({ consulta, selectedOption, selectedOptions, isMultipleSelection, onConfirm, onGoBack, submitting }) {
  const { t } = useTranslation();

  const getSelectedItems = () => {
    if (isMultipleSelection) {
      return selectedOptions.map(optTitle => {
        const opt = consulta.options.find(o => (typeof o === 'string' ? o : o.title) === optTitle);
        return opt || { title: optTitle };
      });
    } else {
      const realTitle = selectedOption.includes(":::") ? selectedOption.split(":::")[0] : selectedOption;
      const opt = consulta.options.find(o => (typeof o === 'string' ? o : o.title) === realTitle);
      return [opt || { title: realTitle }];
    }
  };

  const items = getSelectedItems();

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <CheckCircle2 className="h-7 w-7 text-blue-600" />
        <div>
          <h2 className="text-xl font-heading font-semibold text-slate-900">{t('voting.confirmTitle')}</h2>
          <p className="text-sm text-slate-500">{t('voting.confirmSubtitle')}</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
        <p className="text-sm font-medium text-blue-800 mb-3">
          {isMultipleSelection
            ? t('voting.confirmSelectedMultiple', { count: items.length })
            : t('voting.confirmSelectedSingle')}
        </p>
        {items.map((item, i) => {
          const title = typeof item === 'string' ? item : item.title;
          const desc = typeof item === 'object' ? item.description : null;
          const images = typeof item === 'object' ? (item.image_urls?.length ? item.image_urls : (item.image_url ? [item.image_url] : [])) : [];
          const amount = typeof item === 'object' ? item.amount : null;
          const carouselUrls = images.map(u => `${API_URL}${u}`);

          return (
            <div key={i} className="bg-white rounded-lg border border-blue-100 overflow-hidden">
              {carouselUrls.length > 0 && (
                <ImageCarousel images={carouselUrls} alt={title} maxHeight="max-h-48" />
              )}
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
                  <div>
                    <h3 className="font-semibold text-slate-900">{title}</h3>
                    {desc && <MarkdownText className="text-sm text-slate-500 mt-1">{desc}</MarkdownText>}
                    {amount && <p className="text-sm text-emerald-600 mt-1 font-medium">{amount.toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' })}</p>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1 h-12"
          onClick={onGoBack}
          data-testid="confirm-go-back-btn"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t('voting.confirmGoBack')}
        </Button>
        <Button
          type="button"
          className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={onConfirm}
          disabled={submitting}
          data-testid="confirm-submit-btn"
        >
          {submitting ? (
            <div className="spinner" />
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              {t('voting.confirmSubmit')}
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}
